import React, {
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import SettingsContext from "~/contexts/SettingsContext";
import styles from "./AssetModal.module.css";
import ThemeContext from "~/contexts/ThemeContext";
import Dialog from "~/components/Dialog/Dialog";
import CircleCloseButton from "~/icons/CircleCloseIcon";
import AssetView from "~/modules/loyalty/components/AssetView/AssetView";
import OrderContext from "~/contexts/OrderContext";
import pluralize from "pluralize";

import Spinner from "~/components/Spinner/Spinner";
import { useFetcher } from "@remix-run/react";
import { KEYS } from "~/modules/checkout/constants";
import { action } from "~/routes/$orderSource.$siteId.($table)";
import { action as assetAction } from "~/routes/loyalty.purchaseAsset";
import AuthContext from "~/contexts/AuthContext";
import clsx from "clsx";
import { Asset, AssetInfo } from "~/entities/authEntities";
import { formatLoyaltyBalance } from "~/helpers/numberHelpers";
import { getCooldownMessage } from "~/helpers/promotionHelpers";
import ProgressClubPoints from "../../components/ProgressClubPoints/ProgressClubPoints";
import {
  LoyaltyDialogSubtitleFontSize,
  LoyaltyDialogTitleFontSize,
} from "~/theme/models/themeModels";
import { capitalizeWords } from "~/helpers/stringHelpers";
import {
  cartIsValid,
  generateLine,
  getLineBeforeDiscountPrice,
} from "~/helpers/cartHelpers";
import { sum } from "~/helpers/arrayHelpers";
import { getProductsList } from "~/helpers/menuHelpers";
import MenuContext from "~/contexts/MenuContext";
import {
  OfferPick,
  isFreeDiscount,
  offerPickForPromotion,
} from "~/helpers/offerPicker";
import OfferItemPicker from "~/modules/offers/components/OfferItemPicker/OfferItemPicker";
import DealBuilderContext from "~/contexts/DealBuilderContext";
import SiteContext from "~/contexts/SiteContext";

type PromoModalProps = {
  visible: boolean;
  setVisible: (x: boolean) => void;
  assetInfo?: AssetInfo;
  loading?: boolean;
  error?: string;
};

enum SubmittingAction {
  None,
  Purchase,
  AssetSelection,
  CartModification,
}

const AssetModal: React.FC<PromoModalProps> = ({
  visible,
  setVisible,
  assetInfo,
  loading,
  error,
}) => {
  const { orgSettings } = useContext(SettingsContext);
  const { template } = useContext(ThemeContext);
  const { site } = useContext(SiteContext);
  const siteId = site?.id || null;
  const {
    redeemPromotion,
    orderSummary,
    promotionsRequiringSelection,
    addOrderLines,
    readonlyCart,
  } = useContext(OrderContext);
  const { showFreeItemAdded } = useContext(DealBuilderContext);
  const { authenticatedUser, useComoAuth } = useContext(AuthContext);
  const { menu } = useContext(MenuContext);

  const fetcher = useFetcher<typeof action>({ key: KEYS.SUBMIT_ORDER_FETCHER });
  const assetFetcher = useFetcher<typeof assetAction>();

  const [submitting, setSubmitting] = useState<SubmittingAction>(
    SubmittingAction.None,
  );
  const [selectedAsset, setSelectedAsset] = useState<Asset | null>(null);

  // Styles
  const backgroundColor = template.menuScreen.general.backgroundColour;
  const titleFontColor = template.loyaltyDialog.general.titleFontColour;
  const titleFontSize =
    LoyaltyDialogTitleFontSize[template.loyaltyDialog.general.titleFontSize];
  const subtitleFontColor = template.loyaltyDialog.general.subtitleFontColour;
  const subtitleFontSize =
    LoyaltyDialogSubtitleFontSize[
      template.loyaltyDialog.general.subtitleFontSize
    ];
  const showTargetPoints =
    template.loyaltyDialog.targetPoints.show &&
    !!authenticatedUser?.loyaltyBalance;
  const targetPoints = template.loyaltyDialog.targetPoints.points;

  // Points
  const pointsTextColor = template.loyaltyDialog.general.pointsFontColour;

  // Falls back when the org's name for rewards isn't set (or is blank)
  const promoName = orgSettings?.loyaltySettings?.promotionName || "Reward";

  const fetcherErrorMessage = useMemo(() => {
    const isError =
      fetcher.data && !fetcher?.data?.success && fetcher.state === "idle";

    if (!isError) return null;

    if (!useComoAuth) {
      return fetcher.data?.message || "Something went wrong.";
    }

    const cooldownMessage = getCooldownMessage(promoName);

    return cooldownMessage || fetcher.data?.message || "Something went wrong.";
  }, [fetcher.data, fetcher.state, useComoAuth, promoName]);

  const showAssetFetcherErrorMessage = useMemo(
    () =>
      assetFetcher.data &&
      !assetFetcher?.data?.success &&
      assetFetcher.state == "idle",
    [assetFetcher.data, assetFetcher.state],
  );

  const assets = useMemo(
    () =>
      assetInfo?.assets?.filter(
        (x) => !x.siteId || (siteId && x.siteId === siteId),
      ),
    [assetInfo?.assets, siteId],
  );

  const purchasedAsset = useMemo(
    () => assets?.find((asset) => asset.key === assetFetcher.data?.assetKey),
    [assetFetcher.data, assets],
  );

  const promotionOverrideExists = Object.values(
    orderSummary.promotionSchedulingOverride,
  ).some((x) => x);

  const appliedAsset = useMemo(
    () =>
      // Don't show applied asset if there was a redemption key used
      !orderSummary.redemptionKey || promotionOverrideExists
        ? // Don't show applied asset if any promotion scheduling override is set (this means the promotion exists in dnero and a separate flow is used)
          undefined
        : assets?.find((asset) => asset.key === orderSummary.redemptionKey),
    [orderSummary.redemptionKey, assets, promotionOverrideExists],
  );

  const productsSummary = useMemo(() => {
    const qtyBySku: Record<string, number> = {};
    const priceBySku: Record<string, number> = {};

    orderSummary.lines.forEach((line) => {
      const sku = line.item.product.sku;
      if (sku) {
        qtyBySku[sku] = (qtyBySku[sku] ?? 0) + line.item.qty;
        priceBySku[sku] =
          (priceBySku[sku] ?? 0) + getLineBeforeDiscountPrice(line);
      }
    });

    return { qtyBySku, priceBySku };
  }, [orderSummary.lines]);

  const [hideRequirementSelection, setHideRequirementSelection] =
    useState(false);

  // What the customer still has to choose for the applied reward or code, as picker slots
  const offerPick: OfferPick | null = useMemo(() => {
    if (!menu) {
      return null;
    }
    const products = getProductsList(menu);
    if (
      appliedAsset &&
      appliedAsset.selectionRules &&
      appliedAsset.selectionRules.length
    ) {
      // Find rules which need selections
      for (const rules of appliedAsset.selectionRules) {
        if (rules.logic !== "and") {
          console.error(`Unhandled como selection rule logic: ${rules.logic}`);
          continue;
        }

        for (const requirement of rules.requirements) {
          const totalQty = sum(
            requirement.productSkus.map(
              (sku) => productsSummary.qtyBySku[sku] ?? 0,
            ),
          );

          const totalPrice = sum(
            requirement.productSkus.map(
              (sku) => productsSummary.priceBySku[sku] ?? 0,
            ),
          );

          if (
            totalQty < requirement.requiredQty ||
            totalPrice < requirement.requiredPrice
          ) {
            const items = products.filter((item) =>
              requirement.productSkus.includes(item.productData.sku),
            );
            return {
              offerName: appliedAsset.name,
              text: requirement.text,
              discount: null,
              slots: [
                {
                  key: `como-${requirement.productSkus.join(",")}`,
                  label:
                    items.length === 1 ? items[0].productData.name : "Items",
                  discounted: false,
                  quantity: Math.max(1, requirement.requiredQty - totalQty),
                  inCart: [],
                  items,
                },
              ],
            };
          }
        }
      }
    }

    const pending = promotionsRequiringSelection.find((x) => x.allowModal);
    return pending
      ? offerPickForPromotion(
          pending.promotion,
          orderSummary.lines,
          products,
          appliedAsset?.name || pending.promotion.name,
        )
      : null;
  }, [
    menu,
    appliedAsset,
    productsSummary,
    promotionsRequiringSelection,
    orderSummary.lines,
  ]);

  const onClose = useCallback(() => {
    setSelectedAsset(null);
    setSubmitting(SubmittingAction.None);
    setVisible(false);
    setHideRequirementSelection(false);
  }, [setVisible]);

  const isSelectionRequired = !!offerPick;

  // Auto close modal when...
  useEffect(() => {
    if (
      // We have just tried to add an asset
      (submitting === SubmittingAction.AssetSelection &&
        // No in progress api requests
        fetcher.state == "idle" &&
        // Last request to order api was a success
        fetcher?.data?.success &&
        // You didn't have a purchaseable asset selected
        !selectedAsset &&
        // There's not currently a selection required for this asset
        !isSelectionRequired) ||
      // We have just tried to update the cart
      (submitting === SubmittingAction.CartModification &&
        // There's not currently a selection required for this asset
        !isSelectionRequired)
    ) {
      onClose();
      return;
    }
  }, [
    onClose,
    fetcher.data,
    fetcher.state,
    submitting,
    selectedAsset,
    isSelectionRequired,
  ]);

  const submittingFetcher =
    submitting === SubmittingAction.Purchase ? assetFetcher : fetcher;

  useEffect(() => {
    if (
      submittingFetcher.state == "idle" &&
      submittingFetcher?.data?.success &&
      submitting
    ) {
      setSubmitting(SubmittingAction.None);
    }
  }, [submittingFetcher.data, submittingFetcher.state, submitting]);

  const getBalanceText = () => {
    return (
      authenticatedUser?.loyaltyBalance != null && (
        <span>
          You have{" "}
          <b className={styles.pointsText} style={{ color: pointsTextColor }}>
            {formatLoyaltyBalance(
              orgSettings,
              authenticatedUser?.loyaltyBalance,
            )}{" "}
          </b>
          {orgSettings?.loyaltySettings?.balanceUnitName ?? "Balance"}
        </span>
      )
    );
  };

  const getPointShop = () => {
    if (!assetInfo?.purchaseableAssets?.length) {
      return null;
    }

    return (
      <div className={styles.pointShop}>
        <div className={styles.header}>
          <span
            className={styles.title}
            style={{ color: titleFontColor, fontSize: titleFontSize }}
          >{`${pluralize.plural(capitalizeWords(promoName))} Shop`}</span>
        </div>
        <p className={styles.text} style={{ padding: "8px 0" }}>
          {`Select a ${pluralize.singular(
            promoName.toLowerCase(),
          )} below to add to 'My ${pluralize.plural(
            capitalizeWords(promoName),
          )}'`}
        </p>
        {showTargetPoints && (
          <>
            <div
              className={styles.pointShopProgress}
              style={{ color: subtitleFontColor }}
            >
              <span style={{ fontWeight: 600 }}>
                {formatLoyaltyBalance(
                  orgSettings,
                  authenticatedUser?.loyaltyBalance,
                )}
              </span>
              <span>{` / ${formatLoyaltyBalance(
                orgSettings,
                targetPoints,
              )}`}</span>
            </div>
            <div className={styles.text}>
              {orgSettings?.loyaltySettings?.balanceUnitName ?? "Balance"} until
              your next {promoName}
            </div>
          </>
        )}
        <div className={styles.assetList}>
          {assetInfo?.purchaseableAssets?.map((asset, index) => (
            <AssetView
              key={index}
              asset={asset}
              onSelect={() => {
                setSelectedAsset(asset);
                setSubmitting(SubmittingAction.Purchase);

                const formData = new FormData();
                formData.append("assetId", asset.id!);
                assetFetcher.submit(formData, {
                  action: "/loyalty/purchaseAsset",
                  method: "post",
                });
              }}
              selected={false}
            />
          ))}
        </div>
      </div>
    );
  };

  // While a reward is being applied the order still shows the submitted version, without any items the server adds
  // for it, so the picker would flash up and close again. The dialog's spinner covers it until the order is back.
  const applyingReward =
    submitting === SubmittingAction.AssetSelection && fetcher.state !== "idle";
  const pickerOffer =
    !hideRequirementSelection && !applyingReward ? offerPick : null;

  // A lone free item with nothing to choose goes straight into the order, with a notice that can undo it
  const loneFreeItem = (() => {
    const slot = pickerOffer?.slots.length === 1 ? pickerOffer.slots[0] : null;
    const [item] = slot?.items ?? [];
    return slot &&
      !appliedAsset &&
      slot.discounted &&
      isFreeDiscount(pickerOffer!.discount) &&
      slot.items.length === 1 &&
      slot.quantity === 1 &&
      !item.productData.soldOut &&
      !!menu &&
      // No required choices to make
      cartIsValid(generateLine(item, menu.code).item)
      ? item
      : null;
  })();
  const autoAdded = useRef<string | null>(null);
  useEffect(() => {
    // Not from the placeholder shown while rewards load, which can't tell a reward from a code yet
    if (!visible || loading || !loneFreeItem || !menu || readonlyCart) {
      return;
    }
    // Once per item, until the order settles
    if (autoAdded.current === loneFreeItem.productCode) {
      return;
    }
    autoAdded.current = loneFreeItem.productCode;
    const line = generateLine(loneFreeItem, menu.code);
    line.menuItemCode = null;
    addOrderLines([line]);
    showFreeItemAdded(`Free ${loneFreeItem.productData.name}`, [line.id]);
    onClose();
  }, [
    visible,
    loading,
    loneFreeItem,
    menu,
    readonlyCart,
    addOrderLines,
    showFreeItemAdded,
    onClose,
  ]);
  useEffect(() => {
    if (!loneFreeItem) {
      autoAdded.current = null;
    }
  }, [loneFreeItem]);

  const getModalBody = () => {
    if (
      (submittingFetcher.state !== "idle" &&
        (submitting === SubmittingAction.AssetSelection ||
          submitting === SubmittingAction.Purchase)) ||
      loading
    ) {
      return (
        <div className={styles.modalBody}>
          <Spinner className={styles.spinner} />
        </div>
      );
    }

    if (selectedAsset && purchasedAsset) {
      return (
        <div className={styles.selectedAssetBody}>
          <p
            className={styles.subtitle}
            style={{ color: subtitleFontColor, fontSize: subtitleFontSize }}
          >
            {getBalanceText()} remaining
          </p>
          <p className={styles.pointShopProgress}>
            Nice choice! {pluralize.singular(capitalizeWords(promoName))} has
            been added to &apos;My{" "}
            {pluralize.plural(capitalizeWords(promoName))}&apos;. Would you like
            to use it now?
          </p>
          <AssetView
            asset={purchasedAsset}
            onSelect={() => {
              setHideRequirementSelection(false);
              setSubmitting(SubmittingAction.AssetSelection);
              setSelectedAsset(null);
              redeemPromotion(purchasedAsset.key!);
            }}
            onClose={() => {
              setSelectedAsset(null);
            }}
          />
        </div>
      );
    }

    if (assets?.length === 0) {
      return (
        <div className={styles.modalBody}>
          <p
            className={styles.subtitle}
            style={{ color: subtitleFontColor, fontSize: subtitleFontSize }}
          >
            {getBalanceText()}
          </p>
          {showTargetPoints ? (
            <>
              <ProgressClubPoints
                currentPoints={authenticatedUser?.loyaltyBalance ?? 0}
              />
              <p className={styles.targetDescriptionText}>
                Clock {targetPoints}{" "}
                {orgSettings.loyaltySettings?.balanceUnitName ?? "balance"}{" "}
                (miles per burger) to score 20 Burger Bucks. Find your available
                club goods below
              </p>
            </>
          ) : (
            <p className={styles.text}>
              You have no {pluralize.plural(promoName.toLowerCase())} at the
              moment. Claim them using your{" "}
              {orgSettings?.loyaltySettings?.balanceUnitName ?? "Balance"}{" "}
              below.
            </p>
          )}
          {getPointShop()}
          {showTargetPoints && (
            <div className={styles.bottomText}>
              *One Club Good per order. Not available with other Club Goods,
              Burger Bucks or other BurgerFuel offer.
            </div>
          )}
        </div>
      );
    }

    return (
      <div className={styles.modalBody}>
        <p
          className={styles.subtitle}
          style={{ color: subtitleFontColor, fontSize: subtitleFontSize }}
        >
          {getBalanceText()}
        </p>
        {showTargetPoints ? (
          <>
            <ProgressClubPoints
              currentPoints={authenticatedUser?.loyaltyBalance ?? 0}
            />
            <p className={styles.targetDescriptionText}>
              Clock {targetPoints}{" "}
              {orgSettings.loyaltySettings?.balanceUnitName ?? "balance"} (miles
              per burger) to score 20 Burger Bucks. Find your available club
              goods below
            </p>
          </>
        ) : (
          <p className={styles.text}>
            You&apos;ll find your available{" "}
            {pluralize.plural(promoName.toLowerCase())} below.{" "}
            <span className={styles.limitText}>
              One {pluralize.singular(promoName.toLowerCase())} per visit.
            </span>
          </p>
        )}
        <div className={styles.assetList}>
          {assets?.map((asset, index) => (
            <AssetView
              key={index}
              asset={asset}
              onSelect={() => {
                setHideRequirementSelection(false);
                setSubmitting(SubmittingAction.AssetSelection);
                redeemPromotion(asset.key!);
              }}
              selected={orderSummary.redemptionKey === asset.key}
            />
          ))}
        </div>
        {getPointShop()}
        {showTargetPoints && (
          <div className={styles.bottomText}>
            *One Club Good per order. Not available with other Club Goods,
            Burger Bucks or other BurgerFuel offer.
          </div>
        )}
      </div>
    );
  };

  return (
    <>
      {/* Choosing items for an offer gets the deal builder's layout rather than this dialog */}
      {visible && pickerOffer && !loneFreeItem && (
        <OfferItemPicker
          // A new requirement starts with nothing chosen
          key={pickerOffer.slots
            .map((x) => `${x.key}:${x.quantity}:${x.inCart.length}`)
            .join(",")}
          offer={pickerOffer}
          onAdded={() => setSubmitting(SubmittingAction.CartModification)}
          // Only when there's a list of rewards to go back to
          onBack={
            assets?.length ? () => setHideRequirementSelection(true) : undefined
          }
          onClose={onClose}
        />
      )}
      <Dialog
        visible={visible && !pickerOffer && !loneFreeItem}
        onClose={onClose}
      >
        <div className={styles.content} style={{ backgroundColor }}>
          <div
            className={clsx(
              selectedAsset ? styles.selectedAssetHeader : styles.modalHeader,
            )}
          >
            {!selectedAsset && (
              <span
                className={styles.title}
                style={{
                  color: titleFontColor,
                  fontSize: titleFontSize,
                  paddingLeft: 24,
                }}
              >
                {`My ${pluralize.plural(capitalizeWords(promoName))}`}
              </span>
            )}
            <CircleCloseButton color="#828282" onClick={onClose} />
          </div>
          {getModalBody()}
          <div className={styles.modalFooter}>
            {fetcherErrorMessage && (
              <div className={styles.errorMessage}>{fetcherErrorMessage}</div>
            )}
            {showAssetFetcherErrorMessage && (
              <div className={styles.errorMessage}>
                {assetFetcher.data?.message}
              </div>
            )}
            {error && <div className={styles.errorMessage}>{error}</div>}
          </div>
        </div>
      </Dialog>
    </>
  );
};

export default AssetModal;
